import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { servicesAPI, reviewsAPI, favoritesAPI, reportsAPI, viewsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import StarRating from '../components/StarRating';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatWidget from '../components/ChatWidget';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

function getSellerBadge(providerJoined, providerRating, providerTotalReviews, providerVerified) {
  const rating = parseFloat(providerRating) || 0;
  const reviews = parseInt(providerTotalReviews) || 0;
  const joined = providerJoined ? new Date(providerJoined) : null;
  const monthsOnPlatform = joined ? Math.floor((Date.now() - joined.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 0;

  if (rating >= 4.5 && reviews >= 20 && monthsOnPlatform >= 6)
    return { label: 'Top Seller', color: 'text-yellow-800 bg-yellow-100 border-yellow-200', icon: '🏆', desc: 'Excellent ratings, 20+ reviews, 6+ months' };
  if (rating >= 4.0 && reviews >= 10 && monthsOnPlatform >= 3)
    return { label: 'Trusted', color: 'text-green-700 bg-green-100 border-green-200', icon: '✅', desc: 'Good ratings, 10+ reviews, 3+ months' };
  if (providerVerified)
    return { label: 'Verified', color: 'text-blue-700 bg-blue-100 border-blue-200', icon: '🔵', desc: 'Identity verified by TopInfo' };
  if (monthsOnPlatform >= 1)
    return { label: 'Active', color: 'text-gray-600 bg-gray-100 border-gray-200', icon: '🟢', desc: 'Active on the platform' };
  return { label: 'Just Joined', color: 'text-purple-700 bg-purple-100 border-purple-200', icon: '👤', desc: 'New to TopInfo' };
}

function getMemberDuration(joinDate) {
  if (!joinDate) return null;
  const joined = new Date(joinDate);
  const now = new Date();
  const days = Math.floor((now - joined) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Joined today';
  if (days < 30) return `Member for ${days} days`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Member for ${months} month${months > 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  return `Member for ${years} year${years > 1 ? 's' : ''}`;
}

const ServiceDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainImg, setMainImg] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    Promise.all([
      servicesAPI.getById(id),
      reviewsAPI.getByService(id),
    ]).then(([servRes, revRes]) => {
      setService(servRes.data.service);
      setReviews(revRes.data.reviews || []);
    }).catch(() => navigate('/services'))
      .finally(() => setLoading(false));
    // Record view and get count
    viewsAPI.record({ item_type: 'service', item_id: parseInt(id) })
      .then(res => setViewCount(res.data.views))
      .catch(() => viewsAPI.getCount('service', id).then(r => setViewCount(r.data.views)).catch(() => {}));
    if (user) favoritesAPI.getIds().then(res => {
      setIsFav((res.data.favorites || []).some(f => f.item_type === 'service' && f.item_id === parseInt(id)));
    }).catch(() => {});
  }, [id, navigate, user]);

  const toggleFav = async () => {
    if (!user) return navigate('/login');
    const res = await favoritesAPI.toggle({ item_type: 'service', item_id: parseInt(id) });
    setIsFav(res.data.action === 'added');
  };

  if (loading) return <LoadingSpinner />;
  if (!service) return null;

  const images = typeof service.images === 'string' ? JSON.parse(service.images || '[]') : (service.images || []);
  const phone = service.phone;
  const badge = getSellerBadge(service.provider_joined, service.provider_rating, service.provider_total_reviews, service.provider_verified);
  const memberDuration = getMemberDuration(service.provider_joined);

  return (
    <div className="bg-gray-50/50 min-h-screen">
      <SEO title={service.title} description={service.description?.substring(0, 160)} keywords={`${service.title}, ${service.category_name}, service Rwanda, ${service.province_name || "Kigali"}, ${service.district_name || ""}, hire ${service.category_name} Rwanda, book ${service.category_name} Kigali, TopInfo Rwanda`} type="product" jsonLd={service ? {
              '@context': 'https://schema.org',
              '@type': 'Service',
              name: service.title,
              description: service.description?.substring(0, 200),
              provider: { '@type': 'Person', name: `${service.first_name} ${service.last_name}` },
              areaServed: { '@type': 'Country', name: 'Rwanda' },
              ...(service.price && { offers: { '@type': 'Offer', price: service.price, priceCurrency: 'RWF' } }),
            } : undefined} />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <Link to="/services" className="hover:text-primary-600 transition-colors">Services</Link>
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-gray-900 font-medium line-clamp-1">{service.title}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Images + Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Image Gallery */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
              {images.length > 0 ? (
                <>
                  <div className="relative aspect-[16/10] bg-gray-100">
                    <img src={`${API_URL}${images[mainImg]}`} alt={service.title} className="w-full h-full object-contain" />
                    <button onClick={toggleFav} className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full shadow flex items-center justify-center">
                      <svg className={`w-5 h-5 ${isFav ? 'text-red-500 fill-current' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                    {images.length > 1 && (
                      <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">{mainImg + 1}/{images.length}</span>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto">
                      {images.map((img, i) => (
                        <button key={i} onClick={() => setMainImg(i)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${mainImg === i ? 'border-primary-500' : 'border-transparent'}`}>
                          <img src={`${API_URL}${img}`} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-[16/10] bg-gray-100 flex items-center justify-center text-gray-300">
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{service.title}</h1>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="bg-primary-50 text-primary-700 px-2 py-0.5 rounded text-xs font-medium">{service.category_name}</span>
                    {(service.province_name || service.location) && (
                      <span>📍 {[service.province_name, service.district_name, service.sector_name].filter(Boolean).join(' › ') || service.location}</span>
                    )}
                    <span className="flex items-center gap-1 text-gray-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      {viewCount} view{viewCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary-600">{service.price && Number(service.price) > 0 ? <>{Number(service.price).toLocaleString()} RWF{service.price_type === 'hourly' ? <span className="text-sm font-normal text-gray-400">/hr</span> : null}</> : 'Contact for price'}</p>
                  <p className="text-xs text-gray-400 capitalize">{service.price_type === 'negotiable' ? 'Price is negotiable' : service.price_type}</p>
                </div>
              </div>
              <hr className="my-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{service.description}</p>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3">Reviews ({reviews.length})</h3>
              {reviews.length === 0 ? (
                <p className="text-gray-400 text-sm">No reviews yet</p>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-3 pr-1">
                  {reviews.map(r => (
                    <div key={r.id} className="flex gap-3 border-b pb-3 last:border-0">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                        {r.first_name?.[0]}{r.last_name?.[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{r.first_name} {r.last_name}</span>
                          <StarRating rating={r.rating} readonly size="sm" />
                        </div>
                        {r.review_text && <p className="text-gray-600 text-sm">{r.review_text}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Safety Tips */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5">
              <h3 className="font-semibold text-yellow-800 text-sm mb-2">⚠️ Safety Tips</h3>
              <ul className="text-xs text-yellow-700 space-y-1.5">
                <li className="flex items-start gap-2"><span>🛡️</span><span><strong>Choose a safe meeting spot</strong> — Pick a busy, public area when meeting the provider face to face.</span></li>
                <li className="flex items-start gap-2"><span>💰</span><span><strong>Keep your money until you verify</strong> — Never pay upfront before the service has been confirmed or delivered.</span></li>
                <li className="flex items-start gap-2"><span>🔍</span><span><strong>Check everything first</strong> — Review the provider's ratings and confirm all service details before making payment.</span></li>
              </ul>
            </div>

            {/* Report Button */}
            {user && user.id !== service.provider_id && (
              <div>
                {!showReport && !reportSent && (
                  <button onClick={() => setShowReport(true)} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                    Report this listing
                  </button>
                )}
                {reportSent && <p className="text-sm text-green-600">Report submitted. Thank you!</p>}
                {showReport && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-red-800 text-sm">Report this service</h4>
                    <select value={reportReason} onChange={e => setReportReason(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="">Select reason...</option>
                      <option value="spam">Spam</option>
                      <option value="fraud">Fraud / Scam</option>
                      <option value="inappropriate">Inappropriate content</option>
                      <option value="fake">Fake listing</option>
                      <option value="other">Other</option>
                    </select>
                    <textarea value={reportDesc} onChange={e => setReportDesc(e.target.value)} placeholder="Additional details (optional)" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button onClick={async () => {
                        if (!reportReason) return;
                        try { await reportsAPI.create({ reported_type: 'service', reported_id: service.id, reason: reportReason, description: reportDesc }); setReportSent(true); setShowReport(false); } catch { alert('Failed to submit report'); }
                      }} disabled={!reportReason} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">Submit</button>
                      <button onClick={() => setShowReport(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24 shadow-sm">
              {/* Seller Info */}
              <Link to={`/providers/${service.provider_id}`} className="flex items-center gap-3 mb-3 hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold flex-shrink-0">
                  {service.first_name?.[0]}{service.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{service.first_name} {service.last_name}</p>
                  <div className="flex items-center text-xs text-gray-500">
                    <span className="text-yellow-500 mr-1">★</span>
                    {Number(service.provider_rating || 0).toFixed(1)} rating
                  </div>
                </div>
              </Link>

              {/* Seller Trust Badge */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border mb-3 ${badge.color}`}>
                <span className="text-base">{badge.icon}</span>
                <div>
                  <p className="text-xs font-semibold">{badge.label}</p>
                  <p className="text-[10px] opacity-75">{badge.desc}</p>
                </div>
              </div>
              {memberDuration && (
                <p className="text-[11px] text-gray-400 mb-3 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {memberDuration}
                </p>
              )}

              <Link to={`/providers/${service.provider_id}`}
                className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition text-sm mb-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                View Provider Profile
              </Link>

              {/* Contact Buttons */}
              {phone && (
                <div className="mb-3 space-y-2">
                  <a href={`tel:${phone}`} className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    Call {phone}
                  </a>
                  <a href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in: ${service.title} (${Number(service.price).toLocaleString()} RWF) on TopInfo.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-2.5 rounded-lg font-medium hover:bg-green-600 transition text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                </div>
              )}

              {/* Chat */}
              <ChatWidget sellerId={service.provider_id} sellerName={`${service.first_name} ${service.last_name}`} itemTitle={service.title} />

              {/* Book */}
              {user && String(service.provider_id) !== String(user.id) && (
                <Link to={`/book/${service.id}`}
                  className="block w-full text-center bg-primary-600 text-white py-2.5 rounded-lg font-bold hover:brightness-95 transition mt-3 text-sm">
                  Book This Service
                </Link>
              )}
              {!user && (
                <Link to="/login" className="block w-full text-center bg-primary-600 text-white py-2.5 rounded-lg font-bold hover:brightness-95 transition mt-3 text-sm">
                  Login to Book
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
